/**
 * Shows the map frame `TileGate` approved, whatever Mapbox draws after it.
 *
 * Satellite reels came out with the odd frame missing a large patch of imagery -- the style's dark
 * background where the satellite should be, sometimes over half the frame. Under the load chunked
 * rendering puts on the render box (six renders at once) that was about one frame per reel. It was
 * not the gate: instrumented on the box (2026-10-05, bugs/reel-frame-captured-before-satellite-drawn.md
 * in the knowledge base), every hole frame had been approved on a draw whose tiles were all loaded,
 * whose satellite tiles were all drawn, and whose pixels, read back from the canvas at that moment,
 * matched the renders without a hole. The hole came later. Remotion's screenshot lets Mapbox draw
 * again first -- it always has a repaint pending for an arriving tile or a label -- and that later
 * draw is what was captured.
 *
 * So the approved draw is copied, there and then, into a 2D canvas laid over the map's own. The
 * screenshot shows the copy. Holding Mapbox's later draws back instead was tried, in several ways, and
 * each produced whole black frames now and then; the copy holds nothing back. Nine renders with it had
 * no holes and no black frames, at the same speed as without.
 *
 * Only call this in the same task as the draw being approved -- from Mapbox's `render` event. The
 * map's canvas keeps its drawing buffer only until the browser has shown it (`preserveDrawingBuffer`
 * is off), so a copy taken later can be blank. A frame released without its tiles (`approved` false)
 * hides the copy and shows the live map, as before.
 */
export const showApprovedFrame = (map: any, approved: boolean): void => {
	try {
		const source: HTMLCanvasElement = map.getCanvas();
		let copy: HTMLCanvasElement | undefined = map.__approvedFrame;
		if (!copy) {
			copy = document.createElement("canvas");
			copy.style.position = "absolute";
			copy.style.left = "0";
			copy.style.top = "0";
			copy.style.pointerEvents = "none";
			// Straight after the map's canvas: above the map, below anything drawn over it.
			source.parentNode?.insertBefore(copy, source.nextSibling);
			map.__approvedFrame = copy;
		}
		if (!approved) {
			copy.style.visibility = "hidden";
			return;
		}
		if (copy.width !== source.width || copy.height !== source.height) {
			copy.width = source.width;
			copy.height = source.height;
		}
		copy.style.width = source.style.width || `${source.clientWidth}px`;
		copy.style.height = source.style.height || `${source.clientHeight}px`;
		const context = copy.getContext("2d");
		if (!context) throw new Error("no 2d context");
		context.clearRect(0, 0, copy.width, copy.height);
		context.drawImage(source, 0, 0);
		copy.style.visibility = "visible";
	} catch {
		// Without a copy the live map shows, which is how every frame was captured before.
		if (map?.__approvedFrame) map.__approvedFrame.style.visibility = "hidden";
	}
};
